import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, Notification } from '../../hooks/useNotifications';

interface NotificationPanelProps {
  onClose: () => void;
}

function NotifIcon({ type }: { type: Notification['type'] }) {
  if (type === 'diary_like' || type === 'material_like') {
    return <i className="ri-heart-fill text-red-400 text-sm"></i>;
  }
  if (type === 'group_goal_achieved') {
    return <i className="ri-trophy-fill text-amber-400 text-sm"></i>;
  }
  return <i className="ri-chat-3-line text-teal-400 text-sm"></i>;
}

function NotifItem({ notif, onRead }: { notif: Notification; onRead: (id: string) => void }) {
  const navigate = useNavigate();

  const getLabel = () => {
    switch (notif.type) {
      case 'diary_like': return 'があなたの日記にいいねしました';
      case 'material_like': return 'があなたの資料にいいねしました';
      case 'diary_comment': return 'があなたの日記にコメントしました';
      case 'material_comment': return 'があなたの資料にコメントしました';
      case 'group_goal_achieved': return 'がグループの週間目標を達成しました！';
      default: return '';
    }
  };

  const handleClick = () => {
    onRead(notif.id);
    if (notif.type === 'group_goal_achieved') {
      navigate('/ranking');
    } else if (notif.target_type === 'diary') {
      navigate(`/diary/${notif.target_id}`);
    } else {
      navigate('/materials');
    }
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'たった今';
    if (mins < 60) return `${mins}分前`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}時間前`;
    const days = Math.floor(hours / 24);
    return `${days}日前`;
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left cursor-pointer ${
        !notif.is_read ? 'bg-teal-50/50 dark:bg-teal-900/10' : ''
      }`}
    >
      {/* アバター */}
      <div className="relative flex-shrink-0 mt-0.5">
        {notif.actor_avatar_url ? (
          <img
            src={notif.actor_avatar_url}
            alt={notif.actor_username}
            className="w-9 h-9 rounded-full object-cover"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white text-sm font-semibold">
            {(notif.actor_username || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 flex items-center justify-center bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
          <NotifIcon type={notif.type} />
        </div>
      </div>

      {/* テキスト */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">
          <span className="font-semibold">{notif.actor_username}</span>
          <span className="text-gray-600 dark:text-gray-400">{getLabel()}</span>
        </p>
        {notif.target_title && (
          <p className="text-xs text-teal-600 dark:text-teal-400 mt-0.5 truncate font-medium">
            「{notif.target_title}」
          </p>
        )}
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{timeAgo(notif.created_at)}</p>
      </div>

      {/* 未読ドット */}
      {!notif.is_read && (
        <div className="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0 mt-2"></div>
      )}
    </button>
  );
}

export default function NotificationPanel({ onClose }: NotificationPanelProps) {
  const { notifications, unreadCount, loading, markAllAsRead, markAsRead } = useNotifications();

  // パネル外クリックで閉じる
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-notif-panel]') && !target.closest('[data-notif-trigger]')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      data-notif-panel
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl z-50 overflow-hidden"
      style={{ maxHeight: '480px' }}
    >
      {/* ヘッダー */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <i className="ri-notification-3-line text-gray-700 dark:text-gray-300 text-base"></i>
          <h3 className="text-sm font-bold text-gray-800 dark:text-white">通知</h3>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 bg-teal-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium px-2 py-1 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              すべて既読
            </button>
          )}
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 dark:text-gray-400 text-base"></i>
          </button>
        </div>
      </div>

      {/* 通知リスト */}
      <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12 px-4">
            <div className="w-12 h-12 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-full mx-auto mb-3">
              <i className="ri-notification-off-line text-2xl text-gray-400 dark:text-gray-500"></i>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">通知はありません</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              日記や資料にいいねされると通知が届きます
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {notifications.map(notif => (
              <NotifItem key={notif.id} notif={notif} onRead={markAsRead} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
